import type {MaterialType} from "../types/swc.types.ts";
import {estimateQueuedItem} from "./production-estimate/estimate.ts";
import type {EstimateCatalogs, EstimateEntityTypeData} from "./production-estimate/estimate.ts";

export type CalculatorLocation = 'range' | 'station' | 'facility';
export type FinalRunStrategy = 'spread' | 'full';

export function formatLongProductionDuration(min: number | null, max: number | null): string | null {
    if (min === null || max === null || !Number.isFinite(min) || !Number.isFinite(max) || max <= 100 * 86400) {
        return null;
    }
    const useYears = max >= 365 * 86400;
    const daysPerUnit = useYears ? 365 : 30;
    const format = (seconds: number) => (seconds / (daysPerUnit * 86400)).toLocaleString(undefined, {maximumFractionDigits: 2});
    const lower = format(min);
    const upper = format(max);
    const duration = lower === upper ? lower : `${lower} – ${upper}`;
    const unit = useYears ? 'year' : 'month';
    const label = lower === upper && lower === '1' ? unit : `${unit}s`;
    return `About ${duration} ${label} (${daysPerUnit} days/${unit})`;
}

export function buildProductionPlan(totalAssets: number, locationCount = 1, quantityPerRun = 48, strategy: FinalRunStrategy = 'spread') {
    if (![totalAssets, locationCount, quantityPerRun].every((value) => Number.isSafeInteger(value) && value > 0)) {
        throw new Error('Production quantities and locations must be positive whole numbers.');
    }
    const quantity = Math.min(48, quantityPerRun);
    const fullRounds = Math.floor(totalAssets / (locationCount * quantity));
    const remainder = totalAssets - fullRounds * locationCount * quantity;
    const stages: Array<{phase: 'full' | 'final'; rounds: number; locations: number; quantity: number}> = [];
    if (fullRounds > 0) {
        stages.push({phase: 'full', rounds: fullRounds, locations: locationCount, quantity});
    }
    if (remainder > 0) {
        stages.push({
            phase: 'final',
            rounds: 1,
            locations: strategy === 'spread' ? locationCount : Math.ceil(remainder / quantity),
            quantity: strategy === 'spread' ? Math.ceil(remainder / locationCount) : quantity,
        });
    }
    const produced = stages.reduce((total, stage) => total + stage.rounds * stage.locations * stage.quantity, 0);
    return {
        stages,
        requested: totalAssets,
        produced,
        surplus: produced - totalAssets,
        runs: stages.reduce((total, stage) => total + stage.rounds * stage.locations, 0),
    };
}

export function calculateProductionRequirements(
    product: EstimateEntityTypeData,
    plan: ReturnType<typeof buildProductionPlan>,
    catalogs: EstimateCatalogs,
    materialCatalog: MaterialType[],
    location: CalculatorLocation,
    managementSkill: number | null
) {
    const locations = location === 'range' ? ['station', 'facility'] : [location];
    const stages = plan.stages.map((stage) => {
        const estimates = locations.map((holder) => estimateQueuedItem(
            {entityId: product.id, quantity: stage.quantity}, holder, {}, catalogs,
            {byPlanet: {}, bySystem: {}}, 0, 0, managementSkill
        ).estimate);
        const failed = estimates.find((estimate) => estimate.status !== 'ok');
        return {
            ...stage,
            estimateError: failed ? failed.reason ?? 'Production estimate unavailable' : undefined,
            secondsMin: failed ? null : Math.min(...estimates.map((estimate) => estimate.secondsMin!)),
            secondsMax: failed ? null : Math.max(...estimates.map((estimate) => estimate.secondsMax!)),
        };
    });
    const estimateError = stages.find((stage) => stage.estimateError)?.estimateError;
    const materials = Object.entries(product.materials ?? {})
        .filter(([, quantity]) => quantity > 0)
        .map(([materialId, quantity]) => {
            const material = materialCatalog.find((entry) => entry.id === materialId);
            const total = quantity * plan.produced;
            return {
                materialId,
                name: material?.name ?? materialId,
                perAsset: quantity,
                total,
                weight: material && Number.isFinite(material.weight) && material.weight >= 0 ? total * material.weight : null,
                volume: material && Number.isFinite(material.volume) && material.volume >= 0 ? total * material.volume : null,
            };
        })
        .sort((left, right) => left.name.localeCompare(right.name));
    const cargoComplete = materials.length > 0 && materials.every((row) => row.weight !== null && row.volume !== null);

    return {
        estimateError,
        stages,
        totalSecondsMin: estimateError ? null : stages.reduce((total, stage) => total + stage.secondsMin! * stage.rounds, 0),
        totalSecondsMax: estimateError ? null : stages.reduce((total, stage) => total + stage.secondsMax! * stage.rounds, 0),
        materials,
        weight: cargoComplete ? materials.reduce((total, row) => total + row.weight!, 0) : null,
        volume: cargoComplete ? materials.reduce((total, row) => total + row.volume!, 0) : null,
    };
}
